import { BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, Link, useBCMSContext, useRoute, useRouteData, useStateManager } from "@bcm-dev/bcms-vite";
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView, TableBody, TableColumnContainer, TableColumnView, TableItemContainer, TableItemTextView, TableItemView, TableView } from "components/shared";
import { FormButtonRow } from "components/shared/containers/FormContainer";
import { InfoBanner } from "components/shared/views/ConfirmBannerView";
import { PrimaryButton, SecondaryButton, UICheckbox, UITextInput } from "components/ui";
import Classes, { Participant } from "model";
import React from "react";
import { SVGAsset } from "res";
import MobileACHParticipantsTable from "./components/MobileACHParticipantsTable";
import "./styles.css";
import "./mobile.css";


/**
 * @function ParticipantsACHPage 
 * @returns {React.Component}
 * @exported true
 * @description The main page component for the ParticipantsACHPage.
 */
export default function ParticipantsACHPage(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);

  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };
  const className = props.className?(" "+props.className):"";

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,

      // Checks or unchecks a participant row.
      onParticipantChecked: id => value => {
        // Updated the "isChecked" property.
        data.participants = data.participants.reduce((acc, participant) => {
          if(participant.id === id) { participant.isChecked = value; }
          acc.push(participant);
          return acc;
        }, []);
        BCMSStateManagerCRUD.init(state)
              .update([ "data", "participants" ], data.participants)
              .submit(setState);
      },

      onSelectAllCheckmarksChange: value => {
        data.participants = data.participants.reduce((acc, participant) => {
          participant.isChecked = value;
          acc.push(participant);
          return acc;
        }, []);
        BCMSStateManagerCRUD.init(state)
              .update([ "data", "participants" ], data.participants)
              .submit(setState);
      }
  });

  return (
    <PageContainer className={"participants-ach-page"+className} state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </PageContainer>
  );
}

export const getDefaultData = () => ({
  participant: Participant.init({}),
  participants: Participant.initAll()
});
export const getDefaultUI = () => ({
  // no ui changes for now
  showParticipantIsAddedBanner: false
});

function PageContent(props) {
  const navigate = useRoute();
  const { route } = useRouteData();
  const { data, ui, onSelectAllCheckmarksChange } = useStateManager();
  const atLeastOneRowIsChecked = data.participants.some(participant => participant.isChecked === true);
  const allRowsAreChecked = data.participants.every(participant => participant.isChecked === true);

  const { addedParticipant, participantAdded } = route ? route : {}; 
  const participant = addedParticipant ? addedParticipant : data.participant;

  window.history.replaceState({}, document.title);
  
  const participants = data.participants.reduce((acc, participant, index) => {
    // Time is hardcoded for now
    const minutes = index % 2 === 0 ? 0 : 30;
    participant.createdDate.time = [8, minutes, 0];
    acc.push(<ParticipantsACHTableRow key={ participant.id } participant= { participant } />);
    return acc;
  }, []);

  return(
      <PageContentView>
        { (participantAdded || ui.showParticipantIsAddedBanner) && <InfoBanner dataPoint="ACH_Participants_Added_Banner" text={`The participant "${ participant.nickname }" has been added.`}  /> }
        <CenterContentContainer>
          <ContentHeaderView text="ACH Participants" />
          <FormContainer className="small-form-width">
            <FormRowView text="Group">
              <UITextInput dataPoint={ `ACH_Participants_Group_Input` } />
            </FormRowView>
            <FormButtonRow>
              <DisabledPrompt>
                <SecondaryButton text="Clear" />
              </DisabledPrompt>
              <DisabledPrompt>
                <PrimaryButton text="Filter" />
              </DisabledPrompt>
            </FormButtonRow>
          </FormContainer>
          <div className="show-on-desktop">
            <TableView className="participants-ach-table">
              <TableColumnContainer>
                <TableColumnView className="check-box-header" 
                  text={<UICheckbox value={ allRowsAreChecked ? "checked" : "" } onChange={ onSelectAllCheckmarksChange } />} 
                />
                <TableColumnView 
                  text="Nickname" 
                  icon="ArrowUpIcon" 
                />
                <TableColumnView 
                  text="Unique Identifier" 
                  icon="SortIcon" 
                />
                <TableColumnView 
                  text="Created" 
                  icon="SortIcon" 
                />
                <TableColumnView 
                  text="Group" 
                  icon="SortIcon" 
                />
                <TableColumnView 
                  text="Account Number" 
                />
                <TableColumnView 
                  text="Routing Number" 
                  icon="SortIcon" 
                />
                <TableColumnView 
                  text="Account Type" 
                  icon="SortIcon" 
                />
                <TableColumnView 
                  text="Status" 
                  icon="SortIcon" 
                />
                <TableColumnView />
                <TableColumnView />
              </TableColumnContainer>
              <TableBody>
                { participants }
              </TableBody>
            </TableView>
          </div>
          <div className="show-on-mobile">
            <MobileACHParticipantsTable/>
          </div>
          <div className="main-button-container">
          <DisabledPrompt>
            <PrimaryButton className={atLeastOneRowIsChecked ? "" : "disabled-button" } dataPoint={ `ACH_Participants_Delete_Selected_Participants_Button` } text="Delete Selected Participants" />
          </DisabledPrompt>
            <PrimaryButton dataPoint={ `ACH_Participants_Add_Participants_Button` } text="Add Participants" onClick={ () => navigate("/addACHParticipantWorkflow") }  />
          </div>
        </CenterContentContainer>
      </PageContentView>
  );
}

/**
 * @function ParticipantsACHTableRow
 * @returns {React.Component}
 * @exported false
 * @description The repeated table row for the table in the ParticipantsACHPage.
 * @param {STRING} name - Sets the name of the ACH Participant.
 * @param {STRING} uniqueID - Sets the unique ID of the ACH Participant.
 * @param {STRING} dateCreated - Sets the date the ACH Participant was created.
 * @param {STRING} timeCreated - Sets the time the ACH Participant was created.
 * @param {STRING} group - Sets what group the ACH Participant belongs to.
 * @param {STRING} accountNumber - Sets the ACH Participant's account number.
 * @param {STRING} routingNumber - Sets the ACH Participant's routing number.
 * @param {STRING} accountType - Sets the account type of the ACH Participant.
 * @param {STRING} accountStatus - Sets the ACH Participant's account status.
 * @example
 * <ParticipantsACHPage>
 *   <ParticipantsACHTableRow />
 * </ParticipantsACHPage>
 */
function ParticipantsACHTableRow(props) {
  const { onParticipantChecked } = useStateManager();
  const participant = props.participant ?? {};
  
  return(
    <TableItemContainer>
      <TableItemView className="row-checkbox">
        <UICheckbox value={ participant.isChecked } onChange={ onParticipantChecked(participant.id) } />
      </TableItemView>
      <TableItemTextView 
        text={
          <DisabledPrompt>
              <Link dataPoint={ `ACH_Participants_Row_Nickname_Label_${ participant.id }` }>{ participant.nickname }</Link>
          </DisabledPrompt>
        } 
      />
      <TableItemTextView 
        text={ participant.uniqueID } 
      />
      <TableItemTextView 
        text={
          <>
            <p>{ participant.createdDate.format("m/d/Y") }</p>
            <p>{ `${ participant.createdDate.format('h:i') } AM` }</p>
          </>
        } 
      />
      <TableItemTextView 
        text={ participant.group }
      />
      <TableItemTextView 
        text={ participant.number }
      />
      <TableItemTextView 
        text={ participant.routing }
      />
      <TableItemTextView 
        text={ participant.accountType }
      />
      <TableItemTextView 
        text={ participant.status }
      />
      <TableItemView>
        <DisabledPrompt>
          <SVGAsset id="EditIcon" data-point={ `ACH_Participants_Row_Edit_Icon_${ participant.id }` } className="actionable-icon clickable" />
        </DisabledPrompt>
      </TableItemView>
      <TableItemView>
        <DisabledPrompt>
          <SVGAsset id="TrashcanXIcon" data-point={ `ACH_Participants_Row_Delete_Icon_${ participant.id }` } className="actionable-icon clickable" />
        </DisabledPrompt>
      </TableItemView>
    </TableItemContainer>
  );
}